import { fetchAllPages } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { EquipmentClient } from '@frontend/equipment/api';
import { EquipmentStateName, IEquipment } from '@frontend/equipment/types';
import { ErrorHandler } from '@frontend/error-handler';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage, useLocale } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { PackageStateName, PackageType } from '@frontend/package/types';
import { ProductClient, ProductKitClient } from '@frontend/product/api';
import { IProduct, ProductType } from '@frontend/product/types';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { StockClient } from '@frontend/stock/api';
import { Stock } from '@frontend/stock/types';
import { TransactionStateName, TransactionType } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useMemo, useState } from 'react';

import { useLicenseRepository } from '../../../utils/license-repository';
import { useWizardPackages } from '../../hooks/useWizardPackages';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';
import {
    KitEntry,
    SlotSelection,
    computeExactCoverAmountBounds,
    computeMissingProducts,
    computeSlotCover,
    consumableQuantityInSlot,
    hasExactCoverForAmount
} from './slot-cover';

export interface KitItemAvailability {
    product: IProduct;
    kitQuantity: number;
    required: number;
    available: number;
    isFullyAvailable: boolean;
}

export interface KitAvailabilitySummary {
    required: number;
    available: number;
    isFullyAvailable: boolean;
}

interface WorkflowInfo {
    transactionWf: string;
    pickWf: string;
}

export const useConsumeKit = () => {
    const { updateWizardData, store, onSubmit, onBack, navigationState } = useScreen();
    const { locale } = useLocale();
    const { functionalities } = useLicenseRepository();

    const kit: IProduct | null = store?.data?.selectedProduct ?? null;
    const currentSpotId = navigationState.spot?.id;
    const accountId = navigationState.user?.accountId;

    const transactionContext = useWizardTransaction({
        store,
        wizardId: store?.wizard.id,
        autoCreate: false,
        autoFetch: false
    });

    const { setPackages } = useWizardPackages({
        store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        transactionContext
    });

    const [entries, setEntries] = useState<KitEntry[]>([]);
    const [equipments, setEquipments] = useState<IEquipment[]>([]);
    const [equipmentStocks, setEquipmentStocks] = useState<Stock[]>([]);
    const [consumableStocks, setConsumableStocks] = useState<Stock[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [workflowInfoByApplication, setWorkflowInfoByApplication] = useState<Partial<Record<Application, WorkflowInfo>>>({});
    const [isLoadingWorkflowConfiguration, setIsLoadingWorkflowConfiguration] = useState(false);
    const [hasWorkflowConfigurationError, setHasWorkflowConfigurationError] = useState(false);

    useEffect(() => {
        if (!kit) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const run = async () => {
            setIsLoading(true);
            try {
                const kitEntries = await fetchAllPages((params) => ProductKitClient.fetchProductKits(params), { kit_id: kit.id });
                if (cancelled) return;

                if (kitEntries.length === 0) {
                    setEntries([]);
                    setEquipments([]);
                    setEquipmentStocks([]);
                    setConsumableStocks([]);
                    return;
                }

                const childProductIds = [...new Set(kitEntries.map((e) => e.product_id))];
                const resolvedChildren = await ProductClient.resolveProducts(childProductIds);
                if (cancelled) return;

                const productById = new Map(resolvedChildren.results.map((p) => [p.id, p]));
                const resolvedEntries: KitEntry[] = kitEntries
                    .map((entry) => {
                        const product = productById.get(entry.product_id);
                        return product ? { product, quantity: entry.quantity } : null;
                    })
                    .filter((e): e is KitEntry => e !== null);
                setEntries(resolvedEntries);

                const equipmentChildIds = resolvedEntries.filter((e) => e.product.type === ProductType.EQUIPMENT).map((e) => e.product.id);
                const consumableChildIds = resolvedEntries.filter((e) => e.product.type === ProductType.CONSUMABLE).map((e) => e.product.id);

                if (!accountId || !currentSpotId) {
                    setEquipments([]);
                    setEquipmentStocks([]);
                    setConsumableStocks([]);
                    return;
                }

                const fetchedEquipments =
                    equipmentChildIds.length > 0
                        ? await fetchAllPages((params) => EquipmentClient.fetchEquipments(params), {
                              product_id: equipmentChildIds,
                              account_id: accountId,
                              state: EquipmentStateName.EQUIPMENT_AVAILABLE
                          })
                        : [];
                if (cancelled) return;
                setEquipments(fetchedEquipments);

                const fetchedEquipmentStocks =
                    fetchedEquipments.length > 0
                        ? await fetchAllPages((params) => StockClient.fetchStocks(params), {
                              equipment_id: fetchedEquipments.map((e) => e.id),
                              spot_id: currentSpotId
                          })
                        : [];
                if (cancelled) return;
                setEquipmentStocks(fetchedEquipmentStocks);

                const fetchedConsumableStocks =
                    consumableChildIds.length > 0
                        ? await fetchAllPages((params) => StockClient.fetchStocks(params), {
                              product_id: consumableChildIds,
                              spot_id: currentSpotId
                          })
                        : [];
                if (cancelled) return;
                setConsumableStocks(fetchedConsumableStocks);
            } catch (e: any) {
                if (cancelled) return;
                ErrorHandler.handleError({ error: e, entity_type: CommonMessage.OBJECTS.PRODUCT_KIT.DEFAULT });
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kit?.id, accountId, currentSpotId]);

    const equipmentChildren = useMemo(() => entries.filter((e) => e.product.type === ProductType.EQUIPMENT), [entries]);
    const consumableChildren = useMemo(() => entries.filter((e) => e.product.type === ProductType.CONSUMABLE), [entries]);

    useEffect(() => {
        let cancelled = false;
        const applications = [...(equipmentChildren.length > 0 ? [Application.LENDING] : []), ...(consumableChildren.length > 0 ? [Application.VENDING] : [])];

        setWorkflowInfoByApplication({});
        setHasWorkflowConfigurationError(false);
        if (applications.length === 0) {
            setIsLoadingWorkflowConfiguration(false);
            return;
        }

        const relevantFunctionalities = applications.map((application) => ({
            application,
            functionality: functionalities.find((f) => f.application === application && f.feature === Feature.OUTBOUND_TRANSACTION)
        }));
        if (relevantFunctionalities.some(({ functionality }) => !functionality)) {
            Logger.warn('Missing outbound transaction functionality for kit fulfillment', { applications, kitId: kit?.id });
            setHasWorkflowConfigurationError(true);
            setIsLoadingWorkflowConfiguration(false);
            return;
        }

        setIsLoadingWorkflowConfiguration(true);
        Promise.all(
            relevantFunctionalities.map(async ({ application, functionality }) => {
                const result = await SettingClient.fetchSettings({ entity_type: EntityType.FUNCTIONALITY, entity_id: functionality!.id });
                const transactionWf = result.results.find((s) => s.key === SettingKey.TRANSACTION_WORKFLOW_ID)?.value;
                const pickWf = result.results.find((s) => s.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID)?.value;
                if (typeof transactionWf !== 'string' || typeof pickWf !== 'string') {
                    throw new Error(`Missing workflow settings for ${application}`);
                }
                return [application, { transactionWf, pickWf }] as const;
            })
        )
            .then((workflowEntries) => {
                if (cancelled) return;
                setWorkflowInfoByApplication(Object.fromEntries(workflowEntries));
            })
            .catch((error) => {
                Logger.error('Failed to load workflow settings for kit fulfillment', error);
                if (!cancelled) setHasWorkflowConfigurationError(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingWorkflowConfiguration(false);
            });

        return () => {
            cancelled = true;
        };
    }, [consumableChildren.length, equipmentChildren.length, functionalities, kit?.id]);

    /**
     * Per-product availability at the current spot. Equipment counts distinct
     * stocked equipment instances; consumable sums stock quantities.
     */
    const availabilityByProductId = useMemo(() => {
        const byProduct = new Map<string, number>();
        const equipmentById = new Map(equipments.map((eq) => [eq.id, eq]));
        for (const stock of equipmentStocks) {
            if (!stock.equipment_id) continue;
            const equipment = equipmentById.get(stock.equipment_id);
            if (!equipment) continue;
            byProduct.set(equipment.product_id, (byProduct.get(equipment.product_id) ?? 0) + 1);
        }
        for (const stock of consumableStocks) {
            if (stock.quantity <= 0) continue;
            byProduct.set(stock.product_id, (byProduct.get(stock.product_id) ?? 0) + stock.quantity);
        }
        return byProduct;
    }, [equipments, equipmentStocks, consumableStocks]);

    /**
     * Kit-copy bounds from spot-wide stock, tightened to amounts that have an
     * exact whole-SLOT cover (opening a SLOT takes all of its kit-child stock).
     */
    const amountBounds = useMemo(() => {
        if (entries.length === 0) return { min: 0, max: 0 };
        let stockBound = Infinity;
        for (const entry of entries) {
            if (entry.quantity <= 0) continue;
            const available = availabilityByProductId.get(entry.product.id) ?? 0;
            const possible = Math.floor(available / entry.quantity);
            if (possible < stockBound) stockBound = possible;
        }
        if (stockBound === Infinity || stockBound < 1) return { min: 0, max: 0 };
        return computeExactCoverAmountBounds({
            entries,
            equipments,
            equipmentStocks,
            consumableStocks,
            stockBound
        });
    }, [entries, availabilityByProductId, equipments, equipmentStocks, consumableStocks]);

    const maxAmount = amountBounds.max;
    const minAmount = amountBounds.min;

    const [amount, setAmount] = useState<number>(1);

    /**
     * Entries scaled by the currently selected kit-copy amount, so slot
     * cover and package creation operate on the actually-needed quantities.
     */
    const scaledEntries = useMemo<KitEntry[]>(() => entries.map((e) => ({ product: e.product, quantity: e.quantity * amount })), [entries, amount]);

    const selectedSlots = useMemo(
        () => computeSlotCover({ entries: scaledEntries, equipments, equipmentStocks, consumableStocks }),
        [scaledEntries, equipments, equipmentStocks, consumableStocks]
    );

    const missingProducts = useMemo(() => computeMissingProducts(scaledEntries, selectedSlots), [scaledEntries, selectedSlots]);

    // Snap to a kit-copy count that has an exact cover whenever bounds / cover change.
    useEffect(() => {
        if (minAmount < 1) return;
        if (amount < minAmount || amount > maxAmount || selectedSlots.length === 0) setAmount(minAmount);
    }, [minAmount, maxAmount, amount, selectedSlots.length]);

    const changeAmount = (newAmount: number) => {
        if (newAmount < minAmount || newAmount > maxAmount) return;
        if (!hasExactCoverForAmount({ entries, equipments, equipmentStocks, consumableStocks, amount: newAmount })) return;
        setAmount(newAmount);
    };

    const itemAvailabilities = useMemo<KitItemAvailability[]>(
        () =>
            entries.map((entry) => {
                const rawAvailable = availabilityByProductId.get(entry.product.id) ?? 0;
                const required = entry.quantity * amount;
                const available = Math.min(rawAvailable, required);

                return {
                    product: entry.product,
                    kitQuantity: entry.quantity,
                    required,
                    available,
                    isFullyAvailable: rawAvailable >= required
                };
            }),
        [entries, amount, availabilityByProductId]
    );

    const kitAvailability = useMemo<KitAvailabilitySummary>(() => {
        const required = itemAvailabilities.reduce((sum, item) => sum + item.required, 0);
        const available = itemAvailabilities.reduce((sum, item) => sum + item.available, 0);

        return {
            required,
            available,
            isFullyAvailable: required > 0 && available >= required
        };
    }, [itemAvailabilities]);

    /** Consume only when an exact whole-SLOT cover exists for the selected amount. */
    const hasEquipmentChildren = equipmentChildren.length > 0;
    const transactionApplication = hasEquipmentChildren ? Application.LENDING : Application.VENDING;
    const hasTransactionWorkflow = !!workflowInfoByApplication[transactionApplication]?.transactionWf;

    const canConsumeKit =
        !isLoading &&
        !isLoadingWorkflowConfiguration &&
        !isSubmitting &&
        !hasWorkflowConfigurationError &&
        entries.length > 0 &&
        hasTransactionWorkflow &&
        amount >= minAmount &&
        amount <= maxAmount &&
        selectedSlots.length > 0 &&
        missingProducts.length === 0;

    const children = useMemo(() => entries.map((e) => e.product), [entries]);

    const navigateBack = () => {
        updateWizardData({
            selectedProduct: null,
            selectedProductStocks: [],
            selectedAmount: 1,
            selectedAllowance: null
        });
        onBack();
    };

    /**
     * Consumable: one package location per SLOT that holds that product (qty from that SLOT).
     * Equipment: one package per distinct equipment instance.
     */
    const resolveConsumableLocations = (entry: KitEntry, slots: SlotSelection[]): { slot: SlotSelection; quantity: number }[] => {
        const locations: { slot: SlotSelection; quantity: number }[] = [];
        for (const slot of slots) {
            const quantity = consumableQuantityInSlot(slot, entry.product.id);
            if (quantity > 0) locations.push({ slot, quantity });
        }
        return locations;
    };

    const resolveEquipmentLocations = (entry: KitEntry, slots: SlotSelection[]): { slot: SlotSelection; equipment: IEquipment }[] => {
        const locations: { slot: SlotSelection; equipment: IEquipment }[] = [];
        for (const slot of slots) {
            for (const equipment of slot.equipments) {
                if (equipment.product_id === entry.product.id) locations.push({ slot, equipment });
            }
        }
        return locations;
    };

    const confirmKitConsumption = async () => {
        if (!kit || !canConsumeKit) return;
        if (!accountId || !currentSpotId || !navigationState.iot) return;

        const primarySlot = selectedSlots[0];
        if (!primarySlot) return;

        const hasEquipment = entries.some((e) => e.product.type === ProductType.EQUIPMENT);
        const transactionType = hasEquipment ? TransactionType.LENDING : TransactionType.VENDING;
        const transactionApplication = hasEquipment ? Application.LENDING : Application.VENDING;
        const transactionWf = workflowInfoByApplication[transactionApplication]?.transactionWf;
        if (!transactionWf) return;

        setIsSubmitting(true);
        try {
            await transactionContext.localUpdateTransaction({
                type: transactionType,
                workflow_id: transactionWf,
                spot_id: primarySlot.spot_id,
                module_id: primarySlot.module_id,
                slot_id: primarySlot.slot_id,
                user_id: navigationState.user?.id,
                iot_id: navigationState.iot.id,
                application: transactionApplication,
                feature: Feature.OUTBOUND_TRANSACTION,
                state: TransactionStateName.TRANSACTION_CREATED
            });

            const newPackages: Parameters<typeof setPackages>[0] = [];
            let priority = 1;
            for (const entry of scaledEntries) {
                if (entry.product.type === ProductType.EQUIPMENT) {
                    const locations = resolveEquipmentLocations(entry, selectedSlots);
                    for (const { slot, equipment } of locations) {
                        newPackages.push({
                            type: PackageType.PICK_UP,
                            state: PackageStateName.PACKAGE_CREATED,
                            workflow_id: workflowInfoByApplication[Application.LENDING]?.pickWf,
                            user_id: navigationState.user?.id,
                            product_id: entry.product.id,
                            equipment_id: equipment.id,
                            quantity: 1,
                            spot_id: slot.spot_id,
                            module_id: slot.module_id,
                            slot_id: slot.slot_id,
                            application: Application.LENDING,
                            feature: Feature.OUTBOUND_TRANSACTION,
                            priority: priority++,
                            blocking: false
                        });
                    }
                    continue;
                }
                for (const { slot, quantity } of resolveConsumableLocations(entry, selectedSlots)) {
                    newPackages.push({
                        type: PackageType.PICK_UP,
                        state: PackageStateName.PACKAGE_CREATED,
                        workflow_id: workflowInfoByApplication[Application.VENDING]?.pickWf,
                        user_id: navigationState.user?.id,
                        product_id: entry.product.id,
                        equipment_id: null,
                        quantity,
                        spot_id: slot.spot_id,
                        module_id: slot.module_id,
                        slot_id: slot.slot_id,
                        application: Application.VENDING,
                        feature: Feature.OUTBOUND_TRANSACTION,
                        priority: priority++,
                        blocking: false
                    });
                }
            }

            updateWizardData({
                selectedAmount: amount,
                selectedProductStocks: [],
                selectedAllowance: null
            });
            await setPackages(newPackages);

            const continueScreen = store?.steps?.find((step) => step.screen === ScreenType.CONSUME_KIT && step.trigger === ScreenTrigger.SUBMIT);
            onSubmit(continueScreen);
        } catch (e: any) {
            ErrorHandler.handleError({ error: e, entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT });
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        kit,
        children,
        entries,
        equipmentChildren,
        consumableChildren,
        itemAvailabilities,
        kitAvailability,
        selectedSlots,
        missingProducts,
        canConsumeKit,
        amount,
        maxAmount,
        changeAmount,
        locale,
        isLoading: isLoading || isLoadingWorkflowConfiguration || isSubmitting,
        hasWorkflowConfigurationError,
        navigateBack,
        confirmKitConsumption
    };
};
